// Name tags, speech and thought bubbles, floating numbers and hints, in the Inkwell style. All positions are in
// game pixels (the world transform); the text itself is drawn in ink units, so it stays crisp at any zoom.
import ink from '../ink/ink.js';

const I = ink as any;
const { INK, TAU, FONT, rrect, star4 } = I;
const K = 3;
type BubbleContent = string | { icon: string };

const inInk = (g: CanvasRenderingContext2D, x: number, y: number, fn: () => void) => { g.save(); g.translate(x, y); g.scale(1 / K, 1 / K); fn(); g.restore(); };

/** A name over someone's head; `y` is the top of their head. A coloured pip shows their faction. */
export function nameTag(g: CanvasRenderingContext2D, x: number, y: number, text: string, pip: string | null, self = false, icon: string | null = null): void {
  inInk(g, x, y - 4, () => {
    g.font = `600 ${self ? 14 : 13}px ${FONT}`; g.textAlign = 'center'; g.textBaseline = 'alphabetic';
    const w = g.measureText(text).width, off = icon ? 8 : pip ? 5 : 0;
    if (icon) I.drawIcon(g, icon, -w / 2 - 6, -5, 5.5);
    else if (pip) { g.fillStyle = pip; g.beginPath(); g.arc(-w / 2 - 3, -5, 3.4, 0, TAU); g.fill(); g.lineWidth = 1.2; g.strokeStyle = INK; g.stroke(); }
    g.lineWidth = 4; g.lineJoin = 'round'; g.strokeStyle = 'rgba(251,246,234,0.9)'; g.strokeText(text, off, 0);
    g.fillStyle = self ? '#5a3a20' : INK; g.fillText(text, off, 0);
  });
}

/** A label on the ground or over a place: bigger, with a soft card behind it. */
export function placeTag(g: CanvasRenderingContext2D, x: number, y: number, text: string, sub: string | null, dim = false, hot = false): void {
  inInk(g, x, y, () => {
    g.font = `600 15px ${FONT}`; g.textAlign = 'center';
    const w = Math.max(g.measureText(text).width, sub ? (g.font = `500 11px ${I.MONO}`, g.measureText(sub).width) : 0) + 22;
    g.fillStyle = dim ? 'rgba(236,230,218,0.85)' : 'rgba(251,246,234,0.94)'; rrect(g, -w / 2, -16, w, sub ? 38 : 24, 9); g.fill(); g.lineWidth = 1.8; g.strokeStyle = INK; g.stroke();
    g.font = `600 15px ${FONT}`; g.fillStyle = dim ? '#8a8078' : INK; g.fillText(text, 0, 1);
    if (sub) { g.font = `500 11px ${I.MONO}`; g.fillStyle = hot ? '#c9443a' : dim ? '#8a8078' : '#7a6a5e'; g.fillText(sub, 0, 16); }
  });
}

function emote(g: CanvasRenderingContext2D, id: string, s: number): void {
  g.lineJoin = 'round'; g.lineWidth = 1.6; g.strokeStyle = INK;
  if (id === 'heart') { g.beginPath(); g.moveTo(0, s * 0.8); g.bezierCurveTo(-s * 1.4, -s * 0.2, -s * 0.6, -s * 1.2, 0, -s * 0.4); g.bezierCurveTo(s * 0.6, -s * 1.2, s * 1.4, -s * 0.2, 0, s * 0.8); g.fillStyle = '#ee7a86'; g.fill(); g.stroke(); }
  else if (id === 'star' || id === 'sparkle' || id === 'idea') { star4(g, 0, 0, s, 0, id === 'idea' ? '#f7d35e' : '#f2cf4a'); if (id === 'sparkle') star4(g, s * 0.9, -s * 0.7, s * 0.45, 0, '#fff6d8'); }
  else if (id === 'music') { g.fillStyle = INK; g.beginPath(); g.ellipse(-s * 0.3, s * 0.5, s * 0.4, s * 0.3, -0.4, 0, TAU); g.fill(); g.fillRect(-s * 0.02, -s * 0.9, s * 0.18, s * 1.4); g.fillRect(-s * 0.02, -s * 0.9, s * 0.7, s * 0.2); }
  else if (id === 'sleep') { g.font = `700 ${s * 1.4}px ${FONT}`; g.fillStyle = '#6f88c2'; g.textAlign = 'center'; g.fillText('z', -s * 0.3, s * 0.4); g.font = `700 ${s}px ${FONT}`; g.fillText('z', s * 0.5, -s * 0.2); }
  else if (id === 'alert' || id === 'question') { g.font = `700 ${s * 2}px ${FONT}`; g.fillStyle = id === 'alert' ? '#e05a4c' : '#6f88c2'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(id === 'alert' ? '!' : '?', 0, 1); g.textBaseline = 'alphabetic'; }
  else if (id === 'anger') { g.strokeStyle = '#e05a4c'; g.lineWidth = 2.4; for (let i = 0; i < 4; i++) { g.save(); g.rotate(i * Math.PI / 2); g.beginPath(); g.moveTo(s * 0.25, -s * 0.9); g.quadraticCurveTo(s * 0.3, -s * 0.3, s * 0.9, -s * 0.25); g.stroke(); g.restore(); } }
  else if (id === 'sweat' || id === 'rain') { g.beginPath(); g.moveTo(0, -s); g.quadraticCurveTo(s * 0.8, s * 0.1, 0, s * 0.8); g.quadraticCurveTo(-s * 0.8, s * 0.1, 0, -s); g.fillStyle = '#9fd3ee'; g.fill(); g.stroke(); }
  else if (id === 'coin') { g.beginPath(); g.arc(0, 0, s * 0.8, 0, TAU); g.fillStyle = '#eab84a'; g.fill(); g.stroke(); g.fillStyle = '#f7d35e'; g.beginPath(); g.arc(-s * 0.2, -s * 0.2, s * 0.3, 0, TAU); g.fill(); }
  else { g.fillStyle = INK; for (let i = -1; i <= 1; i++) { g.beginPath(); g.arc(i * s * 0.7, 0, s * 0.22, 0, TAU); g.fill(); } }
}

/** A speech or thought bubble over someone; `y` is the top of their head. `life` 0→1 fades it in and out. */
export function inkBubble(g: CanvasRenderingContext2D, x: number, y: number, kind: 'say' | 'think', content: BubbleContent, life = 0.5, minY = -1e9): void {
  const fade = Math.min(1, life / 0.06, (1 - life) / 0.1); if (fade <= 0) return;
  const rise = (1 - Math.min(1, life / 0.06)) * 3;
  const by = Math.max(minY + 12, y - 9 + rise);
  g.save(); g.globalAlpha *= fade;
  inInk(g, x, by, () => {
    const icon = typeof content === 'object' ? content.icon : null;
    let text = typeof content === 'string' ? content : '';
    g.font = `600 13px ${FONT}`;
    if (text.length > 34) text = text.slice(0, 33) + '…';
    const w = icon ? 34 : Math.max(30, g.measureText(text).width + 20), h = 26;
    g.fillStyle = '#fffaf0'; rrect(g, -w / 2, -h / 2 - 12, w, h, 12); g.fill(); g.lineWidth = 1.8; g.strokeStyle = INK; g.stroke();
    if (kind === 'think') {
      [[2, 7, 4], [-3, 14, 2.6]].forEach(([dx, dy, r]) => { g.beginPath(); g.arc(dx, dy, r, 0, TAU); g.fillStyle = '#fffaf0'; g.fill(); g.lineWidth = 1.6; g.stroke(); });
    } else {
      g.beginPath(); g.moveTo(-5, h / 2 - 12); g.lineTo(-9, h / 2 - 4); g.lineTo(3, h / 2 - 12); g.fillStyle = '#fffaf0'; g.fill(); g.stroke();
      g.fillRect(-5, h / 2 - 14, 8, 3);
    }
    if (icon) { g.save(); g.translate(0, -12); emote(g, icon, 7); g.restore(); }
    else { g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, -12); g.textBaseline = 'alphabetic'; }
  });
  g.restore();
}

/** A floating number or word (a hit, a heal, "dodge!"). `k` is 0→1 over its life. */
export function floatNum(g: CanvasRenderingContext2D, x: number, y: number, text: string, col: string, k: number, big: boolean): void {
  inInk(g, x, y - k * 14 - (big ? Math.sin(Math.min(1, k * 4) * Math.PI) * 3 : 0), () => {
    g.globalAlpha = Math.min(1, (1 - k) * 2.5);
    g.font = `700 ${big ? 26 : 20}px ${FONT}`; g.textAlign = 'center'; g.lineJoin = 'round';
    g.lineWidth = 5; g.strokeStyle = '#fbf6ea'; g.strokeText(text, 0, 0);
    g.lineWidth = 1.4; g.strokeStyle = INK; g.strokeText(text, 0, 0);
    g.fillStyle = col; g.fillText(text, 0, 0);
  });
}

/** The bouncing "!" over someone you can talk to or something you can pick up. */
export function hint(g: CanvasRenderingContext2D, x: number, y: number, now: number): void {
  inInk(g, x, y - Math.abs(Math.sin(now / 260)) * 3, () => {
    g.fillStyle = '#fffaf0'; g.beginPath(); g.arc(0, 0, 11, 0, TAU); g.fill(); g.lineWidth = 1.8; g.strokeStyle = INK; g.stroke();
    g.font = `700 17px ${FONT}`; g.fillStyle = '#d49a32'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('!', 0, 1); g.textBaseline = 'alphabetic';
  });
}

/** A ring on the ground: a battle circle (red: keep out; green: they'd love help; gold: yours). */
export function battleRing(g: CanvasRenderingContext2D, x: number, y: number, r: number, kind: 'busy' | 'help' | 'mine', now: number): void {
  const col = kind === 'help' ? '#7cc576' : kind === 'mine' ? '#e8b64a' : '#e05a4c', pulse = 0.6 + Math.sin(now / 300) * 0.15;
  g.save();
  g.fillStyle = kind === 'help' ? 'rgba(124,197,118,0.10)' : kind === 'mine' ? 'rgba(232,182,74,0.08)' : 'rgba(224,90,76,0.08)';
  g.beginPath(); g.ellipse(x, y, r, r * 0.8, 0, 0, TAU); g.fill();
  g.globalAlpha = pulse; g.setLineDash([6, 5]); g.lineDashOffset = -now / 60; g.lineWidth = 2; g.strokeStyle = col; g.stroke();
  g.setLineDash([]); g.globalAlpha = pulse * 0.6; g.lineWidth = 0.8; g.strokeStyle = INK; g.beginPath(); g.ellipse(x, y, r + 1.2, r * 0.8 + 1, 0, 0, TAU); g.stroke();
  g.restore();
}

export interface InkDialogue { name: string; right: boolean; who: any; tagColor: string; text: string; done: boolean }
function wrapText(g: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const words = text.split(' '), lines: string[] = []; let cur = '';
  for (const w of words) { const t = cur ? cur + ' ' + w : w; if (g.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t; }
  if (cur) lines.push(cur); return lines;
}
/** The conversation box: a paper card along the bottom, the speaker's portrait in a round frame (head and
 *  shoulders, looking at you, with the line's expression), their name on a tab, and the words. In game pixels,
 *  drawn in screen space (vw x vh visible). `who` is an Ink character (render/inkchar.ts). */
export function inkDialogue(g: CanvasRenderingContext2D, vw: number, vh: number, d: InkDialogue, now: number): void {
  const w = Math.min(vw - 12, 330), h = 66, x = (vw - w) / 2, y = Math.max(16, vh - h - 8);
  g.save();
  // the card
  g.fillStyle = 'rgba(62,47,40,0.9)'; rrect(g, x, y + 1.6, w, h, 9); g.fill();
  g.fillStyle = '#fbf6ea'; rrect(g, x, y, w, h, 9); g.fill(); g.lineWidth = 1; g.strokeStyle = INK; g.stroke();
  // the portrait: a circle of soft colour with the speaker drawn big inside, head near the middle
  const pr = 26, pcx = d.right ? x + w - pr - 8 : x + pr + 8, pcy = y + h / 2;
  g.save(); g.beginPath(); g.arc(pcx, pcy, pr, 0, TAU); g.fillStyle = '#efe3c8'; g.fill(); g.clip();
  g.fillStyle = 'rgba(255,255,255,0.45)'; g.beginPath(); g.arc(pcx - 8, pcy - 10, pr * 0.7, 0, TAU); g.fill();
  if (d.who) {
    const top = I.charTop(d.who.def, I.S), z = 2.6;                         // ink units to the top of their head
    // feet below the frame, so the head (the top ~30 units) sits a little above the middle
    const headMid = top - 19, feetY = pcy - 1 + headMid * z / K;   // the middle of their face just above the centre
    const th = d.who.th; d.who.th = Math.PI / 2 + (d.right ? -0.35 : 0.35);
    g.translate(pcx, feetY); g.scale(z / K, z / K); I.drawChar(g, d.who, 0, 0, I.S, now / 1000);
    d.who.th = th;
  }
  g.restore();
  g.beginPath(); g.arc(pcx, pcy, pr, 0, TAU); g.lineWidth = 1.2; g.strokeStyle = INK; g.stroke();
  // the name tab
  g.font = `600 6px ${FONT}`;
  const nw = g.measureText(d.name).width + 12, nx = d.right ? x + w - nw - 10 : x + 10, ny = y - 7;
  g.fillStyle = '#fbf6ea'; rrect(g, nx, ny, nw, 10, 5); g.fill(); g.lineWidth = 0.9; g.strokeStyle = INK; g.stroke();
  g.fillStyle = d.tagColor; g.beginPath(); g.arc(nx + 5, ny + 5, 1.6, 0, TAU); g.fill(); g.lineWidth = 0.5; g.stroke();
  g.fillStyle = INK; g.textBaseline = 'middle'; g.textAlign = 'left'; g.fillText(d.name, nx + 8.5, ny + 5.3);
  // the words
  g.font = `500 6.2px ${FONT}`; g.textBaseline = 'alphabetic'; g.fillStyle = INK;
  const tx = d.right ? x + 10 : x + pr * 2 + 16, tw = w - pr * 2 - 28;
  wrapText(g, d.text, tw).slice(0, 5).forEach((l, i) => g.fillText(l, tx, y + 15 + i * 8.6));
  if (d.done) {   // the "next" arrow, bobbing gently
    const ax = d.right ? x + w - pr * 2 - 22 : x + w - 12, ay = y + h - 9 + Math.sin(now / 260) * 1.2;
    g.beginPath(); g.moveTo(ax - 3, ay - 2); g.lineTo(ax + 3, ay - 2); g.lineTo(ax, ay + 2); g.closePath(); g.fillStyle = '#e07a68'; g.fill(); g.lineWidth = 0.6; g.strokeStyle = INK; g.stroke();
  }
  g.restore();
}
